import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import logoText from "@assets/images/logo/dark-logo-text-nobg.png";

/**
 * ExtensionAuthSuccess - A page shown after successful Google OAuth from the extension
 * This page sends the auth token back to the extension via postMessage and auto-closes
 */
const ExtensionAuthSuccess = () => {
  const location = useLocation();
  const [status, setStatus] = useState("sending"); // sending, success, error
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    // Get token and user from URL state or sessionStorage
    const searchParams = new URLSearchParams(location.search);
    const accessToken = searchParams.get("token");
    const userDataStr = searchParams.get("user");

    // Check if we have opener (extension popup)
    if (window.opener) {
      try {
        let userData = null;
        if (userDataStr) {
          try {
            userData = JSON.parse(decodeURIComponent(userDataStr));
          } catch (e) {
            console.error("Failed to parse user data:", e);
          }
        }

        // Send success message to the extension
        window.opener.postMessage({
          type: "GOOGLE_AUTH_SUCCESS",
          payload: {
            accessToken: accessToken,
            user: userData,
          }
        }, "*");

        setStatus("success");

        // Start countdown to close
        const countdownInterval = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              window.close();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Auto-close after 3 seconds
        setTimeout(() => {
          window.close();
        }, 3000);

        return () => clearInterval(countdownInterval);
      } catch (error) {
        console.error("Error sending message to extension:", error);
        setStatus("error");
      }
    } else {
      // No opener - user navigated here directly
      setStatus("error");
    }
  }, [location]);

  const handleCloseManually = () => {
    window.close();
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
        backgroundColor: "#f9fafb",
        fontFamily: "'Nunito', -apple-system, BlinkMacSystemFont, sans-serif",
        padding: "20px",
      }}
    >
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "16px",
          padding: "40px",
          boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
          textAlign: "center",
          maxWidth: "400px",
          width: "100%",
        }}
      >
        {/* Logo */}
        <img
          src={logoText}
          alt="Copilotly"
          style={{
            height: "40px",
            marginBottom: "24px",
          }}
        />

        {status === "sending" && (
          <>
            <div
              style={{
                width: "48px",
                height: "48px",
                border: "4px solid #e5e7eb",
                borderTop: "4px solid #8b5cf6",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 20px",
              }}
            />
            <style>
              {`
                @keyframes spin {
                  to { transform: rotate(360deg); }
                }
              `}
            </style>
            <h2 style={{ color: "#374151", marginBottom: "8px" }}>
              Completing Sign In...
            </h2>
            <p style={{ color: "#6b7280", fontSize: "14px" }}>
              Please wait while we connect to the extension.
            </p>
          </>
        )}

        {status === "success" && (
          <>
            {/* Success Checkmark */}
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "linear-gradient(135deg, #10b981, #059669)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#fff"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>

            <h2
              style={{
                color: "#059669",
                marginBottom: "8px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              Successfully Logged In!
            </h2>

            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              You can now use Copilotly in your browser.
            </p>

            <p
              style={{
                color: "#9ca3af",
                fontSize: "13px",
                marginBottom: "16px",
              }}
            >
              This window will close in {countdown} second{countdown !== 1 ? "s" : ""}...
            </p>

            <button
              onClick={handleCloseManually}
              style={{
                background: "linear-gradient(to right, #2e32ed, #8b5cf6, #e80b78)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "opacity 0.2s",
              }}
              onMouseOver={(e) => (e.target.style.opacity = "0.9")}
              onMouseOut={(e) => (e.target.style.opacity = "1")}
            >
              Close Window
            </button>
          </>
        )}

        {status === "error" && (
          <>
            {/* Error Icon */}
            <div
              style={{
                width: "64px",
                height: "64px",
                borderRadius: "50%",
                background: "#fee2e2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 20px",
              }}
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#dc2626"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="15" y1="9" x2="9" y2="15" />
                <line x1="9" y1="9" x2="15" y2="15" />
              </svg>
            </div>

            <h2
              style={{
                color: "#dc2626",
                marginBottom: "8px",
                fontSize: "24px",
                fontWeight: "700",
              }}
            >
              Something went wrong
            </h2>

            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              Could not connect to the extension. Please try logging in again from the
              Copilotly extension.
            </p>

            <button
              onClick={handleCloseManually}
              style={{
                background: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Close Window
            </button>
          </>
        )}
      </div>

      {/* Footer */}
      <p
        style={{
          color: "#9ca3af",
          fontSize: "12px",
          marginTop: "24px",
        }}
      >
        © {new Date().getFullYear()} Copilotly™ - Your Personal AI Copilot
      </p>
    </div>
  );
};

export default ExtensionAuthSuccess;
